import { Grid2 as Grid, Card, CardContent, IconButton, Divider, Typography } from "@mui/material"
import { Edit, Delete } from "@mui/icons-material"
import { Revisione } from "../types"
import { InlineField } from "./DataField"
import { useTranslation } from "react-i18next"
import { useIsMobile } from "../hooks/mobile"
import useUserCanWriteAndDelete from "../hooks/permissions"

type RevisioneCardProps = {
  revisione: Revisione
  onDelete: (revisione: Revisione) => void
  onUpdate: (revisione: Revisione) => void
}

const selectStatoFisicoMap: Record<number, string> = {
  0: "revisione.stato_fisico_enum.assenti",
  1: "revisione.stato_fisico_enum.lievi",
  2: "revisione.stato_fisico_enum.medie",
  3: "revisione.stato_fisico_enum.forti",
  4: "revisione.stato_fisico_enum.gravi",
}

//@ts-ignore
function SelectStatoFisicoLabel({ value }: IntrinsicAttributes & { value: number }) {
  const { t } = useTranslation()
  if (!selectStatoFisicoMap[value]) {
    return ""
  }
  return t(selectStatoFisicoMap[value])
}

const adStripMap: Record<number, string> = {
  0: "revisione.adstrip_enum.buono",
  1: "revisione.adstrip_enum.abbastanza_buono",
  1.5: "revisione.adstrip_enum.inizio_decadimento",
  2: "revisione.adstrip_enum.deterioramento_rapido",
  3: "revisione.adstrip_enum.situazione_critica",
}

function SelectAdStripLabel({ value }: { value: number }) {
  const { t } = useTranslation()
  if (!adStripMap[value]) {
    return ""
  }
  return t(adStripMap[value])
}

const coloreMap: Record<string, string> = {
  originale: "revisione.colore_enum.originale",
  decaduto: "revisione.colore_enum.decaduto",
}

function SelectColoreLabel({ value }: { value: string }) {
  const { t } = useTranslation()
  if (!coloreMap[value]) {
    return ""
  }
  return t(coloreMap[value])
}

export default function RevisioneCard({ revisione, onDelete, onUpdate }: RevisioneCardProps) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const userCanWriteAndDelete = useUserCanWriteAndDelete()

  return (
    <Card>
      <CardContent sx={{ position: "relative" }}>
        {userCanWriteAndDelete && !isMobile && (
          <IconButton
            onClick={() => onUpdate(revisione)}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "primary.main",
            }}
            aria-label="edit"
          >
            <Edit />
          </IconButton>
        )}
        <InlineField label={t("revisione.data")} value={revisione.data} />
        <InlineField label={t("revisione.assemblato")} value={revisione.assemblato} boolean />
        <InlineField
          label={t("revisione.nome_assemblaggio")}
          value={revisione.assemblaggio_data?.nome}
        />

        <Divider sx={{ marginY: 2 }} />

        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
          {t("revisione.caratteristiche_supporto")}
        </Typography>

        <InlineField label={t("revisione.note")} value={revisione.note} />

        <Divider sx={{ marginY: 2 }} />
        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
          {t("revisione.stato_fisico_film")}
        </Typography>

        <Grid container spacing={2}>
          <Grid size={6}>
            <InlineField label={t("revisione.colore")} value={<SelectColoreLabel value={revisione.colore} />} />
          </Grid>
          <Grid size={6}>
            <InlineField label={t("revisione.adstrip")} value={<SelectAdStripLabel value={revisione.adstrip} />} />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid size={6}>
            <InlineField label={t("revisione.polvere")} value={<SelectStatoFisicoLabel value={revisione.polvere} />} />
            <InlineField label={t("revisione.graffi")} value={<SelectStatoFisicoLabel value={revisione.graffi} />} />
            <InlineField
              label={t("revisione.bruciature")}
              value={<SelectStatoFisicoLabel value={revisione.bruciature} />}
            />
            <InlineField label={t("revisione.aceto")} value={<SelectStatoFisicoLabel value={revisione.aceto} />} />
          </Grid>
          <Grid size={6}>
            <InlineField
              label={t("revisione.imbarcatura")}
              value={<SelectStatoFisicoLabel value={revisione.imbarcatura} />}
            />
            <InlineField
              label={t("revisione.piegatura")}
              value={<SelectStatoFisicoLabel value={revisione.piegatura} />}
            />
            <InlineField
              label={t("revisione.ondulazione")}
              value={<SelectStatoFisicoLabel value={revisione.ondulazione} />}
            />
            <InlineField
              label={t("revisione.restringimento")}
              value={<SelectStatoFisicoLabel value={revisione.restringimento} />}
            />
            <InlineField label={t("revisione.curling")} value={<SelectStatoFisicoLabel value={revisione.curling} />} />
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
          {t("revisione.interventi_ripristino")}
        </Typography>

        <Grid container spacing={2}>
          <Grid size={6}>
            <InlineField label={t("revisione.giunte_colla")} value={revisione.giunte_colla} />
          </Grid>
          <Grid size={6}>
            <InlineField label={t("revisione.giunte_nastro")} value={revisione.giunte_nastro} />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid size={6}>
            <InlineField label={t("revisione.giunte_colla_rip")} value={revisione.giunte_colla_rip} />
          </Grid>
          <Grid size={6}>
            <InlineField label={t("revisione.giunte_nastro_rip")} value={revisione.giunte_nastro_rip} />
          </Grid>
        </Grid>

        <InlineField label={t("revisione.proiettabile")} value={revisione.proiettabile} boolean />
        <InlineField label={t("revisione.pulizia")} value={revisione.pulizia} boolean />

        <Divider sx={{ marginY: 2 }} />

        <InlineField label={t("revisione.esterna")} value={revisione.esterna} boolean />
        <InlineField label={t("revisione.lab")} value={revisione.lab} />
        <InlineField label={t("revisione.luogo")} value={revisione.luogo_data?.nome} />
        <Grid container spacing={2}>
          <Grid size={6}>
            <InlineField label={t("revisione.uscita")} value={revisione.uscita} />
          </Grid>
          <Grid size={6}>
            <InlineField label={t("revisione.rientro")} value={revisione.rientro} />
          </Grid>
        </Grid>

        <Divider sx={{ marginY: 2 }} />

        <InlineField label={t("revisione.note_rev_esterna")} value={revisione.note_rev} />
        {userCanWriteAndDelete && !isMobile && (
          <IconButton
            onClick={() => onDelete(revisione)}
            sx={{
              position: "absolute",
              bottom: 8,
              right: 8,
              color: "error.main",
            }}
            aria-label="delete"
          >
            <Delete />
          </IconButton>
        )}
      </CardContent>
    </Card>
  )
}
